import { FileIcon } from 'lucide-react';
import {
  type JSX,
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Skeleton } from '@/components/ui/skeleton.js';
import { ContentDrawing } from '@/features/diagrams/DiagramThumbnail.js';
import {
  DIAGRAM_CONTENT_FORMAT,
  DIAGRAM_CONTENT_VERSION,
  type MermaidContent,
} from '@/lib/api/diagrams.js';
import { isAppInFront, onAppForeground } from '@/lib/app-presence.js';
import { useInView } from '@/lib/use-in-view.js';
import { cn } from '@/lib/utils.js';
import {
  fetchWorkspaceArtifactBlob,
  type WorkspaceArtifact,
} from '@/lib/api/workspace-artifacts.js';

import { ArtifactMarkdown } from './ArtifactMarkdown.js';
import { NoPreview, PreviewLoadError } from './NoPreview.js';
import { OfficeDocumentView } from './OfficeDocumentView.js';
import { PdfDocumentView } from './PdfDocumentView.js';
import {
  delimiterFor,
  isMediaPreviewKind,
  isOfficePreviewKind,
  TOO_LARGE_TO_PREVIEW,
  parseDelimited,
  previewByteCap,
  resolvePreviewKind,
  typeLabel,
  type ArtifactPreviewKind,
} from './artifact-display.js';
import { isRasterThumbnailKind } from './thumbnails/render-thumbnail.js';
import { RasterTile, ZipTile } from './thumbnails/ThumbnailTiles.js';
import { listZipEntries, type ZipListing } from './zip-listing.js';
import { ZipTreeView } from './ZipTreeView.js';

/** The diagram renderer, loaded only once a Mermaid file is actually opened. */
const MermaidPreview = lazy(async () => ({
  default: (await import('@/features/diagrams/MermaidPreview.js'))
    .MermaidPreview,
}));

/**
 * One file to preview, wherever it is stored: a workspace artifact, or a file in SharePoint or OneDrive.
 * The preview needs only these: what the file is called and claims to be, its size when the source says,
 * a key naming these exact bytes, and a way to download them.
 */
export interface PreviewFile {
  name: string;
  contentType: string;
  /** Bytes, when the source knows; -1 when it does not. A known size over the cap skips the download. */
  size: number;
  /**
   * Names these exact bytes. A file whose bytes change gets a new key — an artifact's revision is in its
   * key — so the session cache never serves what was there before. Null when the source names no version
   * for the file: it then skips the session cache and is downloaded again each time it is opened.
   */
  cacheKey: string | null;
  /** Downloads the bytes. Called at most once per key while the preview is mounted. */
  loadBytes: () => Promise<Blob>;
}

/** Resolution state of an artifact's fetched content. */
type ContentState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' }
  /** A file whose bytes turned out over its kind's {@link previewByteCap} once fetched. */
  | { status: 'too-large' }
  | {
      status: 'ready';
      url: string | null;
      text: string | null;
      /** A zip archive's entries, read from its directory, and whether that is all of them. */
      listing: ZipListing | null;
      /** Only the head of the blob was decoded, so `text` is not the whole file. */
      truncated: boolean;
    };

/** Cap on the shared blob cache so a long session browsing many files doesn't hold every artifact blob
 * in memory forever. Oldest entry is evicted first — a simple bound, not a real LRU. */
const MAX_CACHED_BLOBS = 50;

/**
 * Module-level cache so an artifact fetched for a tile thumbnail and then reopened in the viewer (or
 * seen again after a grid/table toggle) downloads once per session. Caches the in-flight promise so
 * simultaneous mounts dedupe too; a rejected fetch is evicted so a later mount can retry.
 *
 * The key carries the REVISION, not just the artifact: an artifact's bytes are immutable per revision but
 * the head moves on every save, and nothing invalidates this map. Without the revision in the key an edit
 * left the user looking at what they had just replaced, for the rest of the session.
 */
const blobCache = new Map<string, Promise<Blob>>();

/** Test-only: the cache is module-level and outlives a single test, so clear it between tests that
 * reuse the same workspace/artifact id to avoid one test seeing another's cached blob. */
export function clearArtifactBlobCacheForTests(): void {
  blobCache.clear();
}

/** The bytes behind a cache key, downloaded once per session. A failed download is evicted so a retry can run. */
function getCachedBlob(key: string, load: () => Promise<Blob>): Promise<Blob> {
  const cached = blobCache.get(key);
  if (cached) return cached;
  const promise = load().catch((error: unknown) => {
    blobCache.delete(key);
    throw error;
  });
  blobCache.set(key, promise);
  if (blobCache.size > MAX_CACHED_BLOBS) {
    const oldestKey = blobCache.keys().next().value;
    if (oldestKey !== undefined) blobCache.delete(oldestKey);
  }
  return promise;
}

/** The session-cache key of one artifact revision, shared by the preview, the tile and the editor. */
function artifactCacheKey(
  workspaceId: string,
  artifactId: string,
  revision: number,
): string {
  return `${workspaceId}:${artifactId}:${revision}`;
}

/**
 * One artifact revision's bytes, fetched once per session. Exported because the inline editor reads the
 * same bytes it is about to replace: opening the editor right after previewing a file should not download
 * it twice, and a save moves the head — so the next read carries a different key and cannot be served the
 * text that was just replaced.
 */
export function getCachedArtifactBlob(
  workspaceId: string,
  artifactId: string,
  revision: number,
): Promise<Blob> {
  return getCachedBlob(
    artifactCacheKey(workspaceId, artifactId, revision),
    () => fetchWorkspaceArtifactBlob(workspaceId, artifactId, revision),
  );
}

/** The kinds a preview decodes to text: shown as text, parsed, or handed to a renderer as source. */
const TEXT_DECODED_KINDS: ReadonlySet<ArtifactPreviewKind> = new Set([
  'text',
  'markdown',
  'table',
  'html',
  'svg',
  'mermaid',
]);

/**
 * Whether a kind skips the session cache. An Office file or an archive is read only by its viewer, and up
 * to tens of megabytes per entry would stay in memory long after the viewer closed.
 */
function bypassesSessionCache(kind: ArtifactPreviewKind): boolean {
  return (
    isOfficePreviewKind(kind) || kind === 'zip' || isMediaPreviewKind(kind)
  );
}

/**
 * An SVG's text as a `data:` URL for an `<img>`. Never a `blob:` URL: a blob URL carries the app's origin,
 * so one opened in a new tab would run the drawing's script there, while a `data:` URL cannot be opened as
 * a page at all and in an `<img>` runs no script and fetches nothing.
 */
function svgDataUrl(text: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(text)}`;
}

/** What one fetched blob becomes for its kind: decoded text, an object or data URL, or a zip's listing. */
async function readPreview(
  blob: Blob,
  kind: ArtifactPreviewKind,
  maxTextBytes: number | undefined,
): Promise<Omit<Extract<ContentState, { status: 'ready' }>, 'status'>> {
  const sliceable = readsTextHead(kind);
  const textBlob =
    sliceable && maxTextBytes !== undefined && blob.size > maxTextBytes
      ? blob.slice(0, maxTextBytes)
      : blob;
  const text = TEXT_DECODED_KINDS.has(kind) ? await textBlob.text() : null;
  const listing =
    kind === 'zip'
      ? listZipEntries(new Uint8Array(await blob.arrayBuffer()))
      : null;
  const url =
    kind === 'svg' && text !== null
      ? svgDataUrl(text)
      : kind === 'image' ||
          kind === 'pdf' ||
          isOfficePreviewKind(kind) ||
          isMediaPreviewKind(kind)
        ? URL.createObjectURL(asPreviewable(kind, blob))
        : null;
  return { url, text, listing, truncated: textBlob !== blob };
}

/**
 * Fetch a file's bytes once `enabled` — through the session cache, which dedupes a tile and a viewer of the
 * same bytes, except for the large kinds {@link bypassesSessionCache} names — and turn them into what the
 * given preview kind needs ({@link readPreview}). (HTML is rendered via `<iframe srcdoc>` rather than a blob
 * URL — a `blob:` URL can't load inside the opaque-origin sandbox we require, and srcdoc keeps the content
 * isolated.) An object URL is revoked on cleanup. `none` never fetches. Bytes over the kind's
 * {@link previewByteCap} are reported `too-large`, and a zip whose directory cannot be read is an error.
 * `maxTextBytes`, when set, decodes only the leading slice of a text/table blob — the tile thumbnail passes it
 * so a large file isn't fully read just to show a snippet (HTML is read whole so its markup isn't cut
 * mid-tag; the full viewer leaves it undefined).
 */
function useFileContent(
  file: PreviewFile,
  kind: ArtifactPreviewKind,
  enabled: boolean,
  maxTextBytes?: number,
): ContentState {
  const [state, setState] = useState<ContentState>({ status: 'idle' });
  /** Held in a ref: a caller builds a new loader on every render, and only the key says the bytes changed. */
  const loadRef = useRef(file.loadBytes);
  loadRef.current = file.loadBytes;
  const { cacheKey } = file;
  useEffect(() => {
    if (!enabled || kind === 'none') return;
    let cancelled = false;
    let createdUrl: string | null = null;
    setState({ status: 'loading' });
    const load = (): Promise<Blob> => loadRef.current();
    const bytes =
      cacheKey === null || bypassesSessionCache(kind)
        ? load()
        : getCachedBlob(cacheKey, load);
    const cap = previewByteCap(kind);
    bytes
      .then(async (blob) => {
        if (cancelled) return;
        if (cap !== null && blob.size > cap) {
          setState({ status: 'too-large' });
          return;
        }
        const preview = await readPreview(blob, kind, maxTextBytes);
        if (preview.url?.startsWith('blob:')) createdUrl = preview.url;
        if (cancelled) {
          if (createdUrl) URL.revokeObjectURL(createdUrl);
          return;
        }
        setState({ status: 'ready', ...preview });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [cacheKey, kind, enabled, maxTextBytes]);
  return state;
}

/**
 * The blob to build an object URL from. The content route deliberately answers `application/octet-stream`
 * for everything that is not a raster image — the stored `content_type` is client-supplied and spoofable,
 * so it is never trusted for a header. A blob URL carrying that type is DOWNLOADED by the browser rather
 * than rendered, which is what made the PDF frame save the file instead of showing it.
 *
 * So a PDF is re-typed here, on OUR classification of the artifact rather than on the server's untrusted
 * metadata. Naming a specific, inert type is what keeps this safe: the danger the server guards against is
 * `text/html` executing on the app origin, and a file that only claims to be a PDF is handed to the PDF
 * parser, which will reject it — never to the HTML parser. Images already arrive with a real MIME type.
 */
function asPreviewable(kind: ArtifactPreviewKind, blob: Blob): Blob {
  const type = PREVIEWABLE_TYPES[kind];
  if (type === undefined || blob.type === type) return blob;
  return new Blob([blob], { type });
}

/** The inert types a blob is re-typed to before its object URL is handed to a viewer or a player. */
const PREVIEWABLE_TYPES: Partial<Record<ArtifactPreviewKind, string>> = {
  pdf: 'application/pdf',
  audio: 'audio/mpeg',
  video: 'video/mp4',
};

/** Bytes of a text/table artifact read for the tile thumbnail — enough for the snippet and first rows,
 * capped so a large file isn't fully decoded and parsed off-screen. */
const TILE_PREVIEW_BYTES = 65536;

/**
 * A text, Markdown or table preview reads at most this many bytes, and a table draws at most this many rows.
 * Everything drawn is real DOM: a 20 MB CSV drawn whole froze the page for eight seconds, and a 15 MB
 * Markdown file for five. The start of the file is what a preview is for; the download has the rest.
 */
const TEXT_PREVIEW_BYTES = 1024 * 1024;
const TABLE_PREVIEW_ROWS = 1000;

/** The text up to its last line break — the head of a file cut mid-line, without the broken last line. */
function wholeLinesOf(text: string): string {
  const lastBreak = text.lastIndexOf('\n');
  return lastBreak === -1 ? text : text.slice(0, lastBreak);
}

/** Text kinds whose preview reads only the first {@link TEXT_PREVIEW_BYTES}. */
function readsTextHead(kind: ArtifactPreviewKind): boolean {
  return kind === 'text' || kind === 'markdown' || kind === 'table';
}

/** Said under a text or Markdown preview that shows only the start of its file. A span, so it fits in a `pre`. */
function BeginningOnlyNote(): JSX.Element {
  return (
    <span className="mt-3 block font-sans text-xs text-muted-foreground">
      Showing the beginning of this file. Download it to see all of it.
    </span>
  );
}

/** Render parsed CSV/TSV records as a table. The first record is treated as the header. */
function DelimitedTable({
  rows,
  compact,
}: {
  rows: string[][];
  compact?: boolean;
}): JSX.Element {
  const bodyRows = compact ? rows.slice(1, 6) : rows.slice(1);
  const header = rows[0] ?? [];
  const maxCols = compact ? 4 : undefined;
  const clip = (cells: string[]): string[] =>
    maxCols ? cells.slice(0, maxCols) : cells;

  return (
    <table
      className={
        compact
          ? 'w-full border-collapse text-left text-[8px] leading-tight'
          : 'w-full border-collapse text-left text-xs'
      }
    >
      <thead>
        <tr>
          {clip(header).map((cell, columnIndex) => (
            <th
              key={columnIndex}
              className={
                compact
                  ? 'truncate border-b border-border px-1 py-0.5 font-semibold'
                  : 'border border-border bg-muted/40 px-2 py-1 font-semibold'
              }
            >
              {cell}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {bodyRows.map((cells, rowIndex) => (
          <tr key={rowIndex}>
            {clip(cells).map((cell, columnIndex) => (
              <td
                key={columnIndex}
                className={
                  compact
                    ? 'max-w-16 truncate px-1 py-0.5 text-muted-foreground'
                    : 'border border-border px-2 py-1'
                }
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * The first rows of a table, with a note when there are more. Counted in parsed records, not lines, so a
 * quoted cell that spans lines is one row. `cutShort` means only the head of the file was read: its last
 * record may be half a row, quoted or not, and is dropped rather than shown broken.
 */
function TablePreview({
  text,
  cutShort,
  delimiter,
  scrollBox,
}: {
  text: string;
  cutShort: boolean;
  delimiter: string;
  scrollBox: string;
}): JSX.Element {
  const rows = parseDelimited(text, delimiter);
  if (cutShort) rows.pop();
  const dataRows = Math.max(rows.length - 1, 0);
  const partial = cutShort || dataRows > TABLE_PREVIEW_ROWS;
  const shownRows = Math.min(dataRows, TABLE_PREVIEW_ROWS);
  return (
    <div className={cn(scrollBox, 'rounded-lg border border-border')}>
      <DelimitedTable rows={rows.slice(0, TABLE_PREVIEW_ROWS + 1)} />
      {partial && (
        <p className="border-t border-border px-2 py-1.5 text-xs text-muted-foreground">
          Showing the first {shownRows.toLocaleString('en-US')} rows. Download
          the file to see all of it.
        </p>
      )}
    </div>
  );
}

/** The icon-and-type fallback shown when a tile can't preview (unknown type, not yet in view, error). */
function IconThumb({ artifact }: { artifact: WorkspaceArtifact }): JSX.Element {
  return (
    <span className="flex h-full flex-col items-center justify-center gap-1.5 text-muted-foreground">
      <FileIcon className="size-8" aria-hidden />
      <span className="text-[10px] font-medium tracking-wide">
        {typeLabel(artifact.contentType, artifact.name)}
      </span>
    </span>
  );
}

/** A Mermaid source as the diagram document the drawing helpers take. */
function mermaidContent(source: string): MermaidContent {
  return {
    format: DIAGRAM_CONTENT_FORMAT,
    version: DIAGRAM_CONTENT_VERSION,
    kind: 'mermaid',
    source,
  };
}

/**
 * Whether the app has been in front of the user since the component mounted. It turns true once and stays
 * true, so a drawing that started is not thrown away when the app goes to the background again.
 */
function useAppInFrontOnce(): boolean {
  const [inFront, setInFront] = useState(isAppInFront);
  useEffect(() => {
    if (inFront) return;
    if (isAppInFront()) {
      setInFront(true);
      return;
    }
    return onAppForeground(() => setInFront(true));
  }, [inFront]);
  return inFront;
}

/**
 * A Mermaid tile: the diagram drawn small and fitted to the box. Drawn live — mermaid is cheap and
 * lazy-loaded — but only once the app is in front, so a hidden page or a tab Teams keeps suspended draws
 * nothing.
 */
function MermaidTile({ source }: { source: string }): JSX.Element {
  const content = useMemo(() => mermaidContent(source), [source]);
  const appInFront = useAppInFrontOnce();
  if (!appInFront) return <Skeleton className="h-full w-full rounded-none" />;
  return (
    <span className="block h-full w-full overflow-hidden">
      <ContentDrawing content={content} />
    </span>
  );
}

/**
 * A tile drawn from the file's content, read through the session cache: image, SVG, the top of a rendered
 * HTML page (sandboxed), the first rows of a table, a snippet of text, or a Mermaid diagram. An SVG or
 * Mermaid file whose known size is over its cap keeps its icon and is never fetched.
 */
function ContentTile({
  workspaceId,
  artifact,
  kind,
}: {
  workspaceId: string;
  artifact: WorkspaceArtifact;
  kind: ArtifactPreviewKind;
}): JSX.Element {
  const cap = previewByteCap(kind);
  const tooLarge = cap !== null && artifact.size > cap;
  const state = useFileContent(
    {
      name: artifact.name,
      contentType: artifact.contentType,
      size: artifact.size,
      cacheKey: artifactCacheKey(
        workspaceId,
        artifact.id,
        artifact.headRevision,
      ),
      loadBytes: () =>
        fetchWorkspaceArtifactBlob(
          workspaceId,
          artifact.id,
          artifact.headRevision,
        ),
    },
    kind,
    !tooLarge,
    TILE_PREVIEW_BYTES,
  );
  if (tooLarge) return <IconThumb artifact={artifact} />;
  if (state.status === 'loading' || state.status === 'idle') {
    return <Skeleton className="h-full w-full rounded-none" />;
  }
  if (state.status !== 'ready') return <IconThumb artifact={artifact} />;
  if ((kind === 'image' || kind === 'svg') && state.url) {
    return (
      <img
        src={state.url}
        alt={artifact.name}
        className={cn(
          'h-full w-full',
          kind === 'svg' ? 'object-contain p-2' : 'object-cover',
        )}
      />
    );
  }
  if (state.text === null) return <IconThumb artifact={artifact} />;
  if (kind === 'html') {
    return (
      <iframe
        srcDoc={state.text}
        title={artifact.name}
        sandbox="allow-scripts"
        referrerPolicy="no-referrer"
        tabIndex={-1}
        className="pointer-events-none h-full w-full border-0 bg-white"
      />
    );
  }
  if (kind === 'mermaid') return <MermaidTile source={state.text} />;
  if (kind === 'table') {
    return (
      <span className="block h-full w-full overflow-hidden p-1.5">
        <DelimitedTable
          rows={parseDelimited(
            state.text,
            delimiterFor(artifact.contentType, artifact.name),
          )}
          compact
        />
      </span>
    );
  }
  return (
    <pre className="h-full w-full overflow-hidden whitespace-pre-wrap break-words p-2 text-left text-[8px] leading-tight text-muted-foreground">
      {state.text.slice(0, 500)}
    </pre>
  );
}

/**
 * The tile thumbnail: a compact, non-interactive preview of the artifact's content. A PDF, Office file or zip
 * gets a thumbnail drawn once and cached in the browser under the file's revision ({@link RasterTile},
 * {@link ZipTile}); everything else is drawn live from its content ({@link ContentTile}). Nothing is fetched
 * until the tile scrolls into view, and the file glyph stands in before that, for an unpreviewable type, for
 * a recording, which is never downloaded just for its tile, and
 * on any failure. Each tile is keyed by the revision, so a new version mounts a fresh tile and never paints
 * the previous version's preview, even for one frame.
 */
export function TilePreview({
  workspaceId,
  artifact,
}: {
  workspaceId: string;
  artifact: WorkspaceArtifact;
}): JSX.Element {
  const kind = resolvePreviewKind(artifact.contentType, artifact.name);
  const [ref, inView] = useInView<HTMLSpanElement>();
  const fallback = <IconThumb artifact={artifact} />;
  const tileProps = { workspaceId, artifact, fallback };
  const revision = artifact.headRevision;
  return (
    <span
      ref={ref}
      className="flex h-28 items-center justify-center overflow-hidden border-b border-border bg-muted/20"
    >
      {kind === 'none' || isMediaPreviewKind(kind) || !inView ? (
        fallback
      ) : isRasterThumbnailKind(kind) ? (
        <RasterTile key={revision} {...tileProps} kind={kind} />
      ) : kind === 'zip' ? (
        <ZipTile key={revision} {...tileProps} />
      ) : (
        <ContentTile
          key={revision}
          workspaceId={workspaceId}
          artifact={artifact}
          kind={kind}
        />
      )}
    </span>
  );
}

/**
 * Full-size content of a file for a viewer: image inline, HTML in an `<iframe srcDoc sandbox="allow-scripts">`,
 * table as a scrollable grid, PDF pages drawn with pdf.js, Word, Excel and PowerPoint files drawn by
 * {@link OfficeDocumentView}, markdown as formatted prose ({@link ArtifactMarkdown} — no raw HTML, images
 * degraded to links), an SVG as an image from a `data:` URL, a zip as the tree of files inside it, a Mermaid
 * file as its diagram, an MP3 or MP4 in the browser's own player, text as readable monospace. Types with no safe inline rendering show a short download
 * prompt. It knows nothing about where the file lives — {@link ArtifactContentBody} feeds it a workspace
 * artifact, and the SharePoint and OneDrive viewers feed it their own files.
 *
 * HTML safety: the frame runs with `allow-scripts` but WITHOUT `allow-same-origin`, so agent-/user-
 * supplied markup executes in a unique opaque origin — it cannot reach the app's origin, cookies,
 * storage, or DOM. (`sandbox=""` was too strict: a `blob:` URL can't be read from an opaque-origin
 * frame, so it rendered blank; `srcDoc` sidesteps the blob entirely.)
 */
export function FileContentBody({
  workspaceId,
  file,
  fill = false,
}: {
  /** The workspace a Mermaid block in a Markdown file is exported to. */
  workspaceId: string;
  file: PreviewFile;
  /**
   * Fill the parent box and OWN the vertical scroll, instead of sizing to the content up to 70vh. The
   * side panel passes this: its body is a definite-height flex cell that used to scroll itself, so the
   * content's own scroll box nested a second scrollbar inside the first. The dialog leaves it off — there
   * the content is what gives the dialog its height.
   */
  fill?: boolean;
}): JSX.Element {
  const kind = resolvePreviewKind(file.contentType, file.name);
  /** Checked before the download when the source says how big the file is; otherwise after it. */
  const cap = previewByteCap(kind);
  const tooLarge = cap !== null && file.size > cap;
  const state = useFileContent(
    file,
    kind,
    kind !== 'none' && !tooLarge,
    readsTextHead(kind) ? TEXT_PREVIEW_BYTES : undefined,
  );
  /** Only the head of the file was read, so the preview is not the whole of it. */
  const cutShort = state.status === 'ready' && state.truncated;
  /** The one scroll container of a text-shaped preview — see the `fill` note above. */
  const scrollBox = fill
    ? 'h-full overflow-auto'
    : 'max-h-[70vh] overflow-auto';

  if (kind === 'none')
    return <NoPreview name={file.name} contentType={file.contentType} />;
  if (tooLarge || state.status === 'too-large') {
    return (
      <NoPreview
        name={file.name}
        contentType={file.contentType}
        reason={TOO_LARGE_TO_PREVIEW}
      />
    );
  }
  if (state.status === 'idle' || state.status === 'loading') {
    return (
      <Skeleton className={cn('w-full rounded-lg', fill ? 'h-full' : 'h-72')} />
    );
  }
  if (state.status === 'error') return <PreviewLoadError />;
  if ((kind === 'image' || kind === 'svg') && state.url) {
    return (
      <img
        src={state.url}
        alt={file.name}
        className={cn(
          'mx-auto max-w-full rounded-lg object-contain',
          fill ? 'max-h-full' : 'max-h-[70vh]',
        )}
      />
    );
  }
  if (kind === 'html' && state.text !== null && state.text !== undefined) {
    return (
      <iframe
        srcDoc={state.text}
        title={file.name}
        sandbox="allow-scripts"
        referrerPolicy="no-referrer"
        className={cn(
          'w-full rounded-lg border border-border bg-white',
          fill ? 'h-full' : 'h-[70vh]',
        )}
      />
    );
  }
  if (kind === 'audio' && state.url) {
    return (
      <div className="flex h-full min-h-40 items-center justify-center">
        <audio
          src={state.url}
          controls
          aria-label={file.name}
          className="w-full max-w-xl"
        />
      </div>
    );
  }
  if (kind === 'video' && state.url) {
    return (
      <video
        src={state.url}
        controls
        playsInline
        aria-label={file.name}
        className={cn(
          'mx-auto block max-w-full rounded-lg bg-black',
          fill ? 'h-full' : 'max-h-[70vh]',
        )}
      />
    );
  }
  if (kind === 'pdf' && state.url) {
    /**
     * Pages drawn with pdf.js, not the browser's own viewer. That viewer is a plugin Chromium will not
     * start inside a sandboxed frame — the SPA runs in the Teams tab iframe — and WebKit leaves a `blob:`
     * PDF frame blank, so an `<iframe src={blobUrl}>` was empty exactly where people open decks. A pdf.js
     * text layer over each page canvas keeps text selection and Ctrl+F working.
     */
    return <PdfDocumentView url={state.url} name={file.name} fill={fill} />;
  }
  if (isOfficePreviewKind(kind) && state.url) {
    return (
      <OfficeDocumentView
        url={state.url}
        name={file.name}
        kind={kind}
        fill={fill}
      />
    );
  }
  if (kind === 'markdown' && state.text !== null && state.text !== undefined) {
    return (
      <div className={cn(scrollBox, 'rounded-lg border border-border p-4')}>
        <ArtifactMarkdown
          text={cutShort ? wholeLinesOf(state.text) : state.text}
          mermaidTarget={{
            workspaceId,
            title: file.name.replace(/\.(md|markdown)$/i, ''),
          }}
        />
        {cutShort && <BeginningOnlyNote />}
      </div>
    );
  }
  if (kind === 'table' && state.text !== null && state.text !== undefined) {
    return (
      <TablePreview
        text={state.text}
        cutShort={cutShort}
        delimiter={delimiterFor(file.contentType, file.name)}
        scrollBox={scrollBox}
      />
    );
  }
  if (kind === 'mermaid' && state.text !== null) {
    return (
      <Suspense
        fallback={
          <Skeleton
            className={cn('w-full rounded-lg', fill ? 'h-full' : 'h-72')}
          />
        }
      >
        <MermaidPreview
          source={state.text}
          readOnly
          className={cn(
            'rounded-lg border border-border',
            fill ? 'h-full' : 'h-[70vh]',
          )}
        />
      </Suspense>
    );
  }
  if (kind === 'zip' && state.listing !== null) {
    return <ZipTreeView listing={state.listing} scrollBox={scrollBox} />;
  }
  return (
    <pre
      className={cn(
        scrollBox,
        'rounded-lg border border-border bg-muted/40 p-4 text-xs whitespace-pre-wrap break-words',
      )}
    >
      {cutShort && state.text ? wholeLinesOf(state.text) : state.text}
      {cutShort && <BeginningOnlyNote />}
    </pre>
  );
}

/**
 * Full-size content of a workspace artifact for the viewer dialog and the side panel: {@link FileContentBody}
 * fed the artifact's bytes through the same session cache as its tile and its editor.
 */
export function ArtifactContentBody({
  workspaceId,
  artifact,
  revision,
  fill = false,
}: {
  workspaceId: string;
  artifact: WorkspaceArtifact;
  /**
   * Which stored revision to render. Defaults to the artifact's head — the version history passes an
   * older number so the user sees THAT version's bytes rather than the newest ones. The name, type and
   * size still come from the row, which describes the head; a restore is how an older version becomes
   * the head again.
   */
  revision?: number;
  /** See {@link FileContentBody}. */
  fill?: boolean;
}): JSX.Element {
  const shownRevision = revision ?? artifact.headRevision;
  /**
   * The row's size describes the head, so it can rule out a download only for the head. An older revision
   * is judged by its own bytes once fetched.
   */
  const viewingHead = shownRevision === artifact.headRevision;
  return (
    <FileContentBody
      workspaceId={workspaceId}
      fill={fill}
      file={{
        name: artifact.name,
        contentType: artifact.contentType,
        size: viewingHead ? artifact.size : -1,
        cacheKey: artifactCacheKey(workspaceId, artifact.id, shownRevision),
        loadBytes: () =>
          fetchWorkspaceArtifactBlob(workspaceId, artifact.id, shownRevision),
      }}
    />
  );
}
